/**
 * The webmail's route tree. Two areas, gated in `beforeLoad`:
 *   /login  — anonymous.
 *   /…      — the mailbox, authenticated (session bootstrapped from the refresh cookie once per load).
 *
 * **Paths here are relative to the app's base**, which is `/` at the root of the mail client's own
 * host — so `/$folder` is `/INBOX` in the address bar. The base was `/mail` only while the two apps
 * shared a host, which is why these paths never carried the prefix.
 *
 * There are no admin screens in this tree and no way to reach one from it except the console URL the
 * server returns as `MailAccount.adminUrl`, which is a document navigation into the other application.
 */
import type { QueryClient } from '@tanstack/react-query';
import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
  type RouterHistory,
} from '@tanstack/react-router';
import * as session from '@mailserver/web-shared/session';
import { NotFoundPage } from '@/routes/not-found';

const MailLayout = lazyRouteComponent(() =>
  import('@/routes/layout').then((m) => ({ default: m.MailLayout })),
);
const MailboxPage = lazyRouteComponent(() =>
  import('@/routes/mailbox').then((m) => ({ default: m.MailboxPage })),
);
// One lazy chunk for the whole of Settings: each section is small, and they are visited together.
const settingsPart = (
  name:
    | 'SettingsLayout'
    | 'SettingsIndex'
    | 'GeneralSection'
    | 'ComposeSection'
    | 'VacationSection'
    | 'FiltersSection'
    | 'SecuritySection'
    | 'AiSection'
    | 'CategoriesSection'
    | 'ProfileSection'
    | 'NotificationsSection',
) => lazyRouteComponent(() => import('@/routes/settings').then((m) => ({ default: m[name] })));
const ComposePage = lazyRouteComponent(() =>
  import('@/routes/compose-page').then((m) => ({ default: m.ComposePage })),
);
const ScheduledPage = lazyRouteComponent(() =>
  import('@/routes/send-later').then((m) => ({ default: m.ScheduledPage })),
);
const LoginPage = lazyRouteComponent(() => import('@/routes/login').then((m) => ({ default: m.LoginPage })));

interface RouterContext {
  queryClient: QueryClient;
}

/**
 * **There is no first-boot check here, deliberately.** The setup wizard belongs to the admin panel,
 * and a mail client is the wrong place to send somebody who wanted their inbox — a mailbox owner
 * cannot complete a wizard they do not own. On a server that has never been set up there are no
 * mailboxes, so sign-in fails and says so, which is the truthful answer to the person asking.
 *
 * Test hook: forget the session between renders.
 */
export const resetAppState = () => {
  session.reset();
};

/**
 * `BASE_URL` carries a trailing slash (`/`, or `/x/` under `WEBMAIL_BASE`); the router wants the path
 * without one, and `/` has to become the empty string or every route would be matched one segment deep.
 */
const basepath = import.meta.env.BASE_URL.replace(/\/$/, '');

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFoundPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (s: Record<string, unknown>): { redirect?: string } =>
    typeof s['redirect'] === 'string' ? { redirect: s['redirect'] } : {},
  beforeLoad: async ({ search }) => {
    const st = await session.init();
    if (st.status === 'authenticated') throw redirect({ to: search.redirect ?? '/' });
  },
  component: LoginPage,
});

const mailRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'mail',
  beforeLoad: async ({ location }) => {
    if ((await session.init()).status !== 'authenticated')
      throw redirect({ to: '/login', search: { redirect: location.href } });
  },
  component: MailLayout,
});

// Literal paths must stay inline (a helper would widen them to `string` and break typed links).
const mail = { getParentRoute: () => mailRoute };
const settingsRoute = createRoute({ ...mail, path: '/settings', component: settingsPart('SettingsLayout') });
const settings = { getParentRoute: () => settingsRoute };
const folderRoute = createRoute({
  ...mail,
  path: '/$folder',
  component: MailboxPage,
  // `label` is a label's keyword: the list shows only the mail carrying it (context STORY-0144); `category`
  // is the Inbox's category tab (context STORY-0297), Primary when absent
  validateSearch: (s: Record<string, unknown>): { q?: string; label?: string; category?: string } => ({
    ...(typeof s['q'] === 'string' ? { q: s['q'] } : {}),
    ...(typeof s['label'] === 'string' ? { label: s['label'] } : {}),
    ...(typeof s['category'] === 'string' ? { category: s['category'] } : {}),
  }),
});
/**
 * The header's search, across every folder (context STORY-0289), and a result opened beside the results at
 * its own folder's URL inside it. A static segment, so a folder named exactly `search` loses its own URL to it.
 */
const searchRoute = createRoute({
  ...mail,
  path: '/search',
  component: MailboxPage,
  validateSearch: (s: Record<string, unknown>): { q?: string } =>
    typeof s['q'] === 'string' ? { q: s['q'] } : {},
});
const routeTree = rootRoute.addChildren([
  loginRoute,
  mailRoute.addChildren([
    createRoute({
      ...mail,
      path: '/',
      beforeLoad: () => {
        throw redirect({ to: '/$folder', params: { folder: 'INBOX' } });
      },
    }),
    settingsRoute.addChildren([
      createRoute({
        ...settings,
        path: '/',
        // A wide screen shows the side nav beside a section, so the bare list has nothing to add there:
        // go to General. A phone keeps `/settings` as the list of sections (context STORY-0111).
        beforeLoad: () => {
          if (window.matchMedia?.('(min-width: 768px)').matches) throw redirect({ to: '/settings/general' });
        },
        component: settingsPart('SettingsIndex'),
      }),
      createRoute({ ...settings, path: '/general', component: settingsPart('GeneralSection') }),
      createRoute({ ...settings, path: '/security', component: settingsPart('SecuritySection') }),
      createRoute({ ...settings, path: '/compose', component: settingsPart('ComposeSection') }),
      createRoute({ ...settings, path: '/vacation', component: settingsPart('VacationSection') }),
      createRoute({ ...settings, path: '/categories', component: settingsPart('CategoriesSection') }),
      createRoute({ ...settings, path: '/profile', component: settingsPart('ProfileSection') }),
      createRoute({ ...settings, path: '/notifications', component: settingsPart('NotificationsSection') }),
      createRoute({
        ...settings,
        path: '/filters',
        // _Filter messages like this_ (context STORY-0296): the message a new filter is filled in from
        validateSearch: (s: Record<string, unknown>): { like?: number; in?: string } => ({
          ...(Number.isInteger(Number(s['like'])) && Number(s['like']) > 0
            ? { like: Number(s['like']) }
            : {}),
          ...(typeof s['in'] === 'string' && s['in'] ? { in: s['in'] } : {}),
        }),
        component: settingsPart('FiltersSection'),
      }),
      // _Mail rules_ became Vacation responder and Filters (context STORY-0291); a link to it still lands
      createRoute({
        ...settings,
        path: '/rules',
        beforeLoad: () => {
          throw redirect({ to: '/settings/filters' });
        },
      }),
      createRoute({ ...settings, path: '/ai', component: settingsPart('AiSection') }),
    ]),
    searchRoute.addChildren([createRoute({ getParentRoute: () => searchRoute, path: '/$folder/$uid' })]),
    // A draft full screen (context STORY-0280). Two segments, so no folder name can shadow it.
    createRoute({ ...mail, path: '/compose/$draft', component: ComposePage }),
    // Send later's waiting messages (context STORY-0301). A static segment, so a folder named exactly
    // `scheduled` loses its own URL to it, as one named `search` does.
    createRoute({ ...mail, path: '/scheduled', component: ScheduledPage }),
    folderRoute.addChildren([
      // The open message has a URL (context STORY-0281), so Back closes it and a phone can open one at
      // all. A child route, not a sibling: the mailbox stays mounted, and the list keeps its scroll and
      // its query as messages open and close. It draws nothing itself; the mailbox reads `$uid`.
      createRoute({ getParentRoute: () => folderRoute, path: '/$uid' }),
    ]),
  ]),
]);

export function createAppRouter(queryClient: QueryClient, history?: RouterHistory) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    // Nothing at the root of the mail client's own host; a prefix only if `WEBMAIL_BASE` sets one.
    basepath,
    ...(history ? { history } : {}),
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
